import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { userApi, Follower } from '../api/user'
import { getActiveMentionQuery } from '../utils/mention'

/**
 * 댓글 입력창에서 "@"를 쳤을 때 뜨는 추천 목록.
 *
 * 후보는 맞팔로우(서로 팔로우)만이다. 사진 태그(FriendTagBottomSheet)와 같은 기준이라
 * "태그할 수 있는 사람 = 언급할 수 있는 사람"으로 사용자가 헷갈리지 않는다.
 * 서버도 같은 기준으로 한 번 더 확인하므로, 목록을 우회해 직접 입력해도 맞팔이
 * 아니면 언급으로 인정되지 않는다.
 *
 * 목록은 한 번만 받아 메모리에 두고 걸러 쓴다. 맞팔 수는 많아야 수백 명이라
 * 글자를 칠 때마다 서버에 묻는 것보다 빠르고, 입력 중 깜빡임도 없다.
 */

const MAX_SUGGESTIONS = 8

export interface MentionSuggestion {
  id: string
  username: string
  nickname: string
  profileImage: string | null
}

export function useMentionSuggestions(text: string, caret: number | null) {
  const [mutuals, setMutuals] = useState<MentionSuggestion[]>([])
  const [isLoaded, setIsLoaded] = useState(false)
  const loadingRef = useRef(false)

  /** 커서 앞에서 입력 중인 "@조각". 없으면 목록을 띄우지 않는다. */
  const active = useMemo(() => {
    if (caret === null) {
      return null
    }
    return getActiveMentionQuery(text, caret)
  }, [text, caret])

  // "@"를 처음 칠 때 한 번만 받아온다 (댓글을 안 쓰는 사람에겐 요청이 안 나간다)
  useEffect(() => {
    if (!active || isLoaded || loadingRef.current) {
      return
    }

    loadingRef.current = true
    void (async () => {
      try {
        // 한쪽이 실패해도 나머지로 목록을 만든다
        const [followersResult, followingResult] = await Promise.allSettled([
          userApi.getFollowers(),
          userApi.getFollowing(),
        ])

        const followers =
          followersResult.status === 'fulfilled'
            ? followersResult.value.followers
            : []
        const following =
          followingResult.status === 'fulfilled'
            ? followingResult.value.following
            : []

        // 맞팔: 내가 팔로우하고, 그 사람도 나를 팔로우
        const followerIds = new Set(followers.map((u: Follower) => u.id))
        const mutual = following
          .filter((u) => followerIds.has(u.id))
          .filter((u) => Boolean(u.username))
          .map((u) => ({
            id: u.id,
            username: u.username,
            nickname: u.nickname,
            profileImage: u.profile_image,
          }))

        setMutuals(mutual)
        setIsLoaded(true)
      } catch (error) {
        console.error('언급 대상 목록 조회 실패:', error)
      } finally {
        loadingRef.current = false
      }
    })()
  }, [active, isLoaded])

  /**
   * 아이디·닉네임 어느 쪽으로 쳐도 찾히게 한다.
   * 아이디가 먼저 걸린 사람을 위에 둔다 — "@"를 쳤다면 아이디를 떠올린 것이므로.
   */
  const suggestions = useMemo(() => {
    if (!active) {
      return []
    }

    const keyword = active.keyword.toLowerCase()
    if (keyword.length === 0) {
      return mutuals.slice(0, MAX_SUGGESTIONS)
    }

    const byUsername: MentionSuggestion[] = []
    const byNickname: MentionSuggestion[] = []

    for (const user of mutuals) {
      if (user.username.toLowerCase().startsWith(keyword)) {
        byUsername.push(user)
      } else if (user.nickname.toLowerCase().startsWith(keyword)) {
        byNickname.push(user)
      }
    }

    return [...byUsername, ...byNickname].slice(0, MAX_SUGGESTIONS)
  }, [active, mutuals])

  const reset = useCallback(() => {
    setIsLoaded(false)
    setMutuals([])
  }, [])

  return {
    /** 지금 "@"를 입력 중인가 */
    isActive: active !== null,
    suggestions,
    reset,
  }
}
